/**
 * Tabla tipo planilla: títulos de columna + filtro en el mismo encabezado.
 * @module-isolated analytics-leads-inventario
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatAnalyticsCell } from '../../../shared/utils/format-number.js';

/** Anchos relativos (table-layout: fixed) para caber en el panel sin cortar columnas. */
const COL_WIDTH = {
  contactId: '7%',
  name: '13%',
  phone: '9%',
  tags: '15%',
  email: '14%',
  leadsCount: '7%',
  gicClientName: '12%',
  vinculo: '8%',
  createdAt: '15%'
};

function HeaderSelect({ label, value, options, onChange, disabled, active }) {
  return (
    <label className={'contacts-inv-th-filter' + (active ? ' is-active' : '')}>
      <span className="visually-hidden">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        aria-label={'Filtrar ' + label}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function HeaderTagsMulti({ options, value, onChange, disabled }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedSet = useMemo(() => new Set((value || []).map(String)), [value]);
  const n = (value || []).length;
  const summary = !options.length ? '—' : !n ? 'Todas' : n === 1 ? value[0] : n + ' sel.';

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle(optId) {
    const next = new Set(selectedSet);
    if (next.has(optId)) next.delete(optId);
    else next.add(optId);
    onChange(Array.from(next));
  }

  return (
    <div
      className={'contacts-inv-th-filter contacts-inv-th-tags' + (n ? ' is-active' : '')}
      ref={rootRef}
    >
      <button
        type="button"
        className={'contacts-inv-th-tags-btn' + (open ? ' is-open' : '')}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={id}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <span id={id}>{summary}</span>
        <span aria-hidden>▾</span>
      </button>
      {open ? (
        <div className="contacts-inv-th-tags-panel" role="listbox" aria-multiselectable="true">
          <div className="contacts-inv-th-tags-actions">
            <button
              type="button"
              className="analytics-btn-link"
              onClick={() => onChange((options || []).map((o) => o.id))}
            >
              Todas
            </button>
            <button type="button" className="analytics-btn-link" onClick={() => onChange([])}>
              Limpiar
            </button>
          </div>
          {(options || []).map((o) => (
            <label key={o.id} className="contacts-inv-th-tags-option">
              <input
                type="checkbox"
                checked={selectedSet.has(o.id)}
                onChange={() => toggle(o.id)}
              />
              <span>
                {o.label}
                {o.count != null ? ' (' + o.count + ')' : ''}
              </span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function HeaderCreado({
  preset,
  presets,
  customFrom,
  customTo,
  onPreset,
  onFrom,
  onTo,
  disabled
}) {
  const active = preset !== 'all';
  return (
    <div className={'contacts-inv-th-filter contacts-inv-th-creado' + (active ? ' is-active' : '')}>
      <select
        value={preset}
        disabled={disabled}
        onChange={(e) => onPreset(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        aria-label="Filtrar creado"
      >
        {presets.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
      {preset === 'custom' ? (
        <div className="contacts-inv-th-creado-custom">
          <input
            type="date"
            value={customFrom}
            disabled={disabled}
            onChange={(e) => onFrom(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Creado desde"
          />
          <input
            type="date"
            value={customTo}
            disabled={disabled}
            onChange={(e) => onTo(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Creado hasta"
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {{
 *   columns: Array<{key:string,label:string,align?:string,sortable?:boolean,format?:string}>,
 *   rows: Array<Record<string, unknown>>,
 *   emptyText?: string,
 *   sortKey?: string,
 *   sortDir?: 'asc'|'desc',
 *   onSort?: (key:string)=>void,
 *   disabled?: boolean,
 *   filters: {
 *     tags: { options: Array, value: string[], onChange: Function },
 *     hasLeads: { value: string, onChange: Function },
 *     hasGic: { value: string, onChange: Function },
 *     vinculo: { value: string, onChange: Function },
 *     creado: {
 *       preset: string,
 *       presets: Array,
 *       customFrom: string,
 *       customTo: string,
 *       onPreset: Function,
 *       onFrom: Function,
 *       onTo: Function
 *     }
 *   }
 * }} props
 */
export default function ContactsInventarioTable({
  columns,
  rows,
  emptyText = 'Sin datos disponibles.',
  sortKey,
  sortDir,
  onSort,
  disabled = false,
  filters
}) {
  const cols = Array.isArray(columns) ? columns : [];
  const list = Array.isArray(rows) ? rows : [];
  const canSort = typeof onSort === 'function';
  const f = filters || {};

  function renderFilter(key) {
    if (key === 'tags' && f.tags) {
      return (
        <HeaderTagsMulti
          options={f.tags.options || []}
          value={f.tags.value || []}
          onChange={f.tags.onChange}
          disabled={disabled}
        />
      );
    }
    if (key === 'leadsCount' && f.hasLeads) {
      return (
        <HeaderSelect
          label="Leads"
          value={f.hasLeads.value}
          active={f.hasLeads.value !== 'all'}
          disabled={disabled}
          onChange={f.hasLeads.onChange}
          options={[
            { id: 'all', label: 'Todos' },
            { id: 'yes', label: 'Con' },
            { id: 'no', label: 'Sin' }
          ]}
        />
      );
    }
    if (key === 'gicClientName' && f.hasGic) {
      return (
        <HeaderSelect
          label="Cliente GIC"
          value={f.hasGic.value}
          active={f.hasGic.value !== 'all'}
          disabled={disabled}
          onChange={f.hasGic.onChange}
          options={[
            { id: 'all', label: 'Todos' },
            { id: 'yes', label: 'Con' },
            { id: 'no', label: 'Sin' }
          ]}
        />
      );
    }
    if (key === 'vinculo' && f.vinculo) {
      return (
        <HeaderSelect
          label="Vínculo"
          value={f.vinculo.value}
          active={f.vinculo.value !== 'all'}
          disabled={disabled}
          onChange={f.vinculo.onChange}
          options={[
            { id: 'all', label: 'Todos' },
            { id: 'ok', label: 'OK' },
            { id: 'no', label: 'Sin OK' }
          ]}
        />
      );
    }
    if (key === 'createdAt' && f.creado) {
      return (
        <HeaderCreado
          preset={f.creado.preset}
          presets={f.creado.presets || []}
          customFrom={f.creado.customFrom}
          customTo={f.creado.customTo}
          onPreset={f.creado.onPreset}
          onFrom={f.creado.onFrom}
          onTo={f.creado.onTo}
          disabled={disabled}
        />
      );
    }
    return null;
  }

  return (
    <div className="analytics-panel analytics-panel-scroll contacts-inv-sheet">
      {!list.length ? (
        <p className="analytics-empty">{emptyText}</p>
      ) : null}
      <table className="analytics-table contacts-inv-table">
        <thead>
          <tr>
            {cols.map((c) => {
              const sortable = canSort && c.sortable !== false;
              const active = sortKey === c.key;
              const filterNode = renderFilter(c.key);
              const sortMark = active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
              return (
                <th
                  key={c.key}
                  style={COL_WIDTH[c.key] ? { width: COL_WIDTH[c.key] } : undefined}
                  className={
                    (c.align === 'right' ? 'num ' : '') +
                    'contacts-inv-th' +
                    (filterNode ? ' has-filter' : '')
                  }
                  aria-sort={
                    !sortable
                      ? undefined
                      : active
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                  }
                >
                  <div className="contacts-inv-th-title">
                    {sortable ? (
                      <button
                        type="button"
                        className={
                          'analytics-th-sort-btn' + (active ? ' is-active' : '')
                        }
                        onClick={() => onSort(c.key)}
                      >
                        {c.label}
                        {sortMark}
                      </button>
                    ) : (
                      <span>{c.label}</span>
                    )}
                  </div>
                  {filterNode}
                </th>
              );
            })}
          </tr>
        </thead>
        {list.length ? (
          <tbody>
            {list.map((row, i) => (
              <tr key={row.id || i}>
                {cols.map((c) => {
                  const raw = row[c.key];
                  const shown =
                    raw == null || raw === '' ? '—' : formatAnalyticsCell(raw, c);
                  return (
                    <td
                      key={c.key}
                      className={c.align === 'right' ? 'num' : undefined}
                      title={shown === '—' ? undefined : String(shown)}
                    >
                      {shown}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        ) : null}
      </table>
    </div>
  );
}
